/**
 * NAWHAL'S SAUCE RUSH — V1 Back to School
 * Mini-jeu de réflexe : grille évolutive (3x3 -> 4x4 -> 5x5), tape les sauces,
 * évite les pièges. Le nombre de sauces simultanées augmente avec la difficulté.
 * Difficulté et combo n'ont pas de plafond : au-delà du dernier palier défini,
 * le jeu continue d'accélérer et le multiplicateur continue de grimper, donc
 * il n'existe pas de score maximum atteignable. Sauce dorée rare (3x points)
 * et bonus temps (⏳ +10s) ajoutent de la variance d'une partie à l'autre.
 * Vanilla JS, aucune dépendance.
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  /* =========================================================================
   * 1. CONFIGURATION — tout ce qui se modifie facilement est ici
   * ====================================================================== */

  var GAME_DURATION = 30; // secondes
  var BASE_POINTS = 10;
  var WRONG_CLICK_PENALTY = 20;

  var SPEED_LEVELS = {
    beginner: 1200,
    medium: 900,
    hard: 700,
    insane: 550
  };

  // La sauce à taper. `image` prioritaire, `emoji` = fallback si pas d'image.
  var targetSauce = {
    name: 'Algérienne',
    image: '',
    emoji: '🧴'
  };

  // Les pièges Back to School.
  var trapItems = [
    { name: 'Livre', image: '', emoji: '📚' },
    { name: 'Crayon', image: '', emoji: '✏️' },
    { name: 'Calculatrice', image: '', emoji: '🧮' },
    { name: 'Réveil', image: '', emoji: '⏰' },
    { name: 'Sac à dos', image: '', emoji: '🎒' },
    { name: 'Contrôle', image: '', emoji: '📝' },
    { name: 'Règle', image: '', emoji: '📏' },
    { name: 'Gomme', image: '', emoji: '🧽' }
  ];

  // Sons optionnels (URLs). Vide = petit bip synthétisé (WebAudio), zéro fichier.
  var SOUNDS = {
    good: '',
    wrong: '',
    record: '',
    golden: '',
    bonus: ''
  };

  /**
   * Paliers de difficulté, par ordre croissant de sauces réussies.
   * `grid`    : côté de la grille (3 = 3x3, 4 = 4x4, 5 = 5x5)
   * `bottles` : sauces à ramasser dans le tour (toutes doivent être prises)
   * `traps`   : nombre de pièges affichés
   * `delay`   : temps pour ramasser UNE sauce (voir BOTTLE_TIME_FACTOR)
   * Règle de lisibilité : bottles + traps doit rester bien sous grid².
   */
  var LEVELS = [
    { hits: 0, grid: 3, bottles: 1, traps: 1, delay: SPEED_LEVELS.beginner },
    { hits: 5, grid: 3, bottles: 1, traps: 2, delay: SPEED_LEVELS.beginner },
    { hits: 10, grid: 4, bottles: 1, traps: 3, delay: SPEED_LEVELS.medium },
    { hits: 18, grid: 4, bottles: 2, traps: 4, delay: SPEED_LEVELS.medium },
    { hits: 26, grid: 4, bottles: 2, traps: 5, delay: SPEED_LEVELS.hard },
    { hits: 34, grid: 5, bottles: 3, traps: 6, delay: SPEED_LEVELS.hard },
    { hits: 44, grid: 5, bottles: 3, traps: 8, delay: SPEED_LEVELS.insane }
  ];

  // Temps ajouté par sauce supplémentaire dans le tour (0.7 = +70% du délai).
  var BOTTLE_TIME_FACTOR = 0.7;

  /**
   * Au-delà du dernier palier de LEVELS, la grille/le nombre de sauces et de
   * pièges restent figés (lisibilité), mais le délai continue de baisser par
   * paliers de POST_MAX_HITS_STEP hits, sans jamais descendre sous le plancher.
   * Combiné à COMBO_STEPS illimité ci-dessous, il n'y a plus de score plafond :
   * un joueur plus rapide peut toujours battre un ancien record.
   */
  var POST_MAX_HITS_STEP = 10;
  var POST_MAX_DELAY_DECAY = 0.94; // -6% de délai par palier au-delà du max
  var MIN_ROUND_DELAY = 350; // plancher : en dessous, la case n'est plus lisible

  // Paliers de combo (série de bonnes réponses consécutives -> multiplicateur).
  // Au-delà du dernier palier, +1 de multiplicateur tous les EXTRA_COMBO_STEP hits, sans plafond.
  var COMBO_STEPS = [
    { streak: 0, multiplier: 1 },
    { streak: 5, multiplier: 2 },
    { streak: 10, multiplier: 3 },
    { streak: 20, multiplier: 4 },
    { streak: 30, multiplier: 5 }
  ];
  var EXTRA_COMBO_STEP = 15;

  /**
   * Sauce dorée : variante rare d'une des sauces du tour, rapporte plus de
   * points. GOLDEN_SAUCE_CHANCE s'applique indépendamment à chaque sauce
   * placée (0.12 = 12% de chances par sauce).
   */
  var GOLDEN_SAUCE_CHANCE = 0.12;
  var GOLDEN_SAUCE_MULTIPLIER = 3;

  /**
   * Bonus temps : item rare (case libre en plus des sauces/pièges) qui ajoute
   * du temps à la partie sans compter comme une sauce ratée ou un piège.
   */
  var TIME_BONUS_CHANCE = 0.1; // par tour
  var TIME_BONUS_SECONDS = 10;
  var timeBonusItem = { name: 'Bonus temps', image: '', emoji: '⏳' };

  var FEEDBACK_MS = 140; // durée max des flashs de case
  var BEST_SCORE_KEY = 'nawhalsSauceRushBestScore';
  var SOUND_KEY = 'nawhalsSauceRushSound';
  var PLAYER_KEY = 'nawhalsSauceRushPlayer';
  var BOARD_KEY = 'nawhalsSauceRushBoard';
  var BOARD_MAX = 5; // lignes de classement affichées en fin de partie

  /**
   * Webhook Google Apps Script (partagé avec le jeu Beach Party). `jeu`
   * distingue les lignes Back to School dans la feuille commune.
   * Renseigné depuis Liquid via data-sr-config.
   */
  var WEBHOOK = { url: '', token: '', game: 'back-to-school' };

  /* Meta Pixel : renseigné depuis Liquid. Vide = on réutilise le fbq que
     Shopify a déjà chargé sur la page, sans en initialiser un second. */
  var PIXEL = { id: '' };

  /* =========================================================================
   * 2. UTILITAIRES
   * ====================================================================== */

  function randInt(max) {
    return Math.floor(Math.random() * max);
  }

  function pickOne(arr) {
    return arr[randInt(arr.length)];
  }

  function storageGet(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function storageSet(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch (e) {
      /* mode privé / quota : le jeu continue sans high score */
    }
  }

  function formatScore(n) {
    return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  }

  function storageGetJSON(key, fallback) {
    try {
      return JSON.parse(storageGet(key)) || fallback;
    } catch (e) {
      return fallback;
    }
  }

  function isEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  // « Eric Brill » -> « E.B. » : un classement public n'a pas à exposer
  // les noms complets des joueurs.
  function initialsOf(prenom, nom) {
    var a = String(prenom || '').trim().charAt(0).toUpperCase();
    var b = String(nom || '').trim().charAt(0).toUpperCase();
    return (a ? a + '.' : '') + (b ? b + '.' : '') || '—';
  }

  /**
   * Meta Pixel. Sur une page à layout Shopify normal, fbq est déjà chargé :
   * on ne réinitialise que si un ID est fourni ET que fbq manque, pour ne
   * jamais compter deux fois un PageView.
   */
  function initPixel() {
    if (window.fbq || !PIXEL.id) return;
    /* eslint-disable */
    !function (f, b, e, v, n, t, s) {
      if (f.fbq) return; n = f.fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n; n.push = n; n.loaded = !0; n.version = '2.0';
      n.queue = []; t = b.createElement(e); t.async = !0;
      t.src = v; s = b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t, s);
    }(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
    /* eslint-enable */
    window.fbq('init', PIXEL.id);
    window.fbq('track', 'PageView');
  }

  function track(event, params, custom) {
    try {
      if (!window.fbq) return;
      window.fbq(custom ? 'trackCustom' : 'track', event, params || {});
    } catch (e) {
      /* le pixel ne doit jamais casser une partie */
    }
  }

  // mm:ss — lisible tel quel dans une cellule de tableur.
  function formatDuration(ms) {
    var total = Math.round(ms / 1000);
    var m = Math.floor(total / 60);
    var s = total % 60;
    return m + ':' + (s < 10 ? '0' : '') + s;
  }

  /* -------------------------------------------------------------------------
   * Joueur & webhook — inscription puis remontée des scores vers Google Sheets
   * ---------------------------------------------------------------------- */

  function getPlayer() {
    var p = storageGetJSON(PLAYER_KEY, null);
    return p && p.email ? p : null;
  }

  function savePlayer(player) {
    storageSet(PLAYER_KEY, JSON.stringify(player));
  }

  /**
   * Envoi en GET no-cors : la réponse n'est pas lisible, mais c'est ce qui
   * permet d'écrire dans la feuille sans configurer CORS côté Apps Script.
   * Un échec réseau ne doit jamais interrompre la partie.
   */
  function sendToSheet(status, extra) {
    if (!WEBHOOK.url) return;
    var player = getPlayer() || {};
    var payload = {
      token: WEBHOOK.token,
      jeu: WEBHOOK.game,
      status: status,
      prenom: player.prenom || '',
      nom: player.nom || '',
      email: player.email || '',
      pays: player.pays || '',
      date: new Date().toLocaleString('fr-FR')
    };
    Object.keys(extra || {}).forEach(function (key) {
      payload[key] = extra[key];
    });
    try {
      var params = new URLSearchParams(payload);
      fetch(WEBHOOK.url + '?' + params.toString(), { mode: 'no-cors' }).catch(function () {});
    } catch (e) {
      /* réseau indisponible : le jeu continue */
    }
  }

  /**
   * Classement en ligne, lu dans la feuille via JSONP.
   * Un Web App Apps Script redirige vers googleusercontent.com, ce qui fait
   * échouer un fetch CORS : la balise <script> reste le seul canal fiable.
   * Le serveur ne renvoie que des initiales — aucun nom complet ni email
   * ne transite jusqu'au navigateur.
   */
  function loadOnlineBoard(limit, done) {
    if (!WEBHOOK.url) return done(null);

    var name = 'srBoard' + Date.now() + Math.floor(Math.random() * 1000);
    var script = document.createElement('script');
    var timer = null;

    function cleanup() {
      clearTimeout(timer);
      try {
        delete window[name];
      } catch (e) {
        window[name] = undefined;
      }
      if (script.parentNode) script.parentNode.removeChild(script);
    }

    window[name] = function (data) {
      cleanup();
      done(data && data.ok ? data.top : null);
    };

    script.onerror = function () {
      cleanup();
      done(null);
    };

    // Feuille injoignable : on ne laisse pas l'écran de fin attendre.
    timer = setTimeout(function () {
      cleanup();
      done(null);
    }, 6000);

    var params = new URLSearchParams({
      jeu: WEBHOOK.game,
      status: 'leaderboard',
      limit: String(limit),
      callback: name
    });
    script.src = WEBHOOK.url + '?' + params.toString();
    document.head.appendChild(script);
  }

  /* Classement local, utilisé en repli quand la feuille est injoignable. */
  function readBoard() {
    var board = storageGetJSON(BOARD_KEY, []);
    return Array.isArray(board) ? board : [];
  }

  function pushBoard(entry) {
    var board = readBoard();
    board.push(entry);
    board.sort(function (a, b) {
      return (b.score || 0) - (a.score || 0);
    });
    board = board.slice(0, 20);
    storageSet(BOARD_KEY, JSON.stringify(board));
    return board;
  }

  // Dernier palier atteint dans un tableau trié par ordre croissant.
  function stepFor(steps, value, key) {
    var current = steps[0];
    for (var i = 1; i < steps.length; i++) {
      if (value >= steps[i][key]) current = steps[i];
    }
    return current;
  }

  // Palier de difficulté courant. Au-delà du dernier palier défini, le délai
  // continue de baisser par paliers réguliers au lieu de rester figé.
  function levelFor(hits) {
    var level = stepFor(LEVELS, hits, 'hits');
    var lastLevel = LEVELS[LEVELS.length - 1];
    if (level !== lastLevel) return level;

    var extraSteps = Math.floor((hits - lastLevel.hits) / POST_MAX_HITS_STEP);
    if (extraSteps <= 0) return level;

    var delay = Math.max(MIN_ROUND_DELAY, lastLevel.delay * Math.pow(POST_MAX_DELAY_DECAY, extraSteps));
    return {
      hits: lastLevel.hits,
      grid: lastLevel.grid,
      bottles: lastLevel.bottles,
      traps: lastLevel.traps,
      delay: delay
    };
  }

  // Multiplicateur de combo courant. Au-delà du dernier palier, +1 tous les
  // EXTRA_COMBO_STEP hits, sans plafond.
  function multiplierFor(streak) {
    var step = stepFor(COMBO_STEPS, streak, 'streak');
    var lastStep = COMBO_STEPS[COMBO_STEPS.length - 1];
    if (step !== lastStep) return step.multiplier;
    return lastStep.multiplier + Math.floor((streak - lastStep.streak) / EXTRA_COMBO_STEP);
  }

  /* =========================================================================
   * 3. AUDIO — désactivable, jamais bloquant
   * ====================================================================== */

  function createAudio() {
    var enabled = storageGet(SOUND_KEY) !== 'off';
    var ctx = null;
    var buffers = {};

    Object.keys(SOUNDS).forEach(function (key) {
      if (SOUNDS[key]) {
        var el = new Audio(SOUNDS[key]);
        el.preload = 'auto';
        buffers[key] = el;
      }
    });

    var TONES = {
      good: { freq: 880, dur: 0.07, type: 'square' },
      wrong: { freq: 160, dur: 0.14, type: 'sawtooth' },
      record: { freq: 1320, dur: 0.18, type: 'triangle' },
      golden: { freq: 1180, dur: 0.12, type: 'triangle' },
      bonus: { freq: 660, dur: 0.1, type: 'sine' }
    };

    function beep(key) {
      var tone = TONES[key];
      if (!tone) return;
      try {
        if (!ctx) {
          var Ctx = window.AudioContext || window.webkitAudioContext;
          if (!Ctx) return;
          ctx = new Ctx();
        }
        if (ctx.state === 'suspended') ctx.resume();
        var osc = ctx.createOscillator();
        var gain = ctx.createGain();
        osc.type = tone.type;
        osc.frequency.value = tone.freq;
        gain.gain.setValueAtTime(0.08, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + tone.dur);
        osc.connect(gain).connect(ctx.destination);
        osc.start();
        osc.stop(ctx.currentTime + tone.dur);
      } catch (e) {
        /* audio indisponible : on ignore */
      }
    }

    return {
      isEnabled: function () {
        return enabled;
      },
      toggle: function () {
        enabled = !enabled;
        storageSet(SOUND_KEY, enabled ? 'on' : 'off');
        return enabled;
      },
      play: function (key) {
        if (!enabled) return;
        var el = buffers[key];
        if (el) {
          try {
            el.currentTime = 0;
            var p = el.play();
            if (p && p.catch) p.catch(function () {});
          } catch (e) {
            /* ignoré */
          }
          return;
        }
        beep(key);
      }
    };
  }

  /* =========================================================================
   * 4. JEU
   * ====================================================================== */

  function initGame(root) {
    if (root.dataset.srReady === '1') return;
    root.dataset.srReady = '1';

    applyInlineConfig(root);

    var el = {
      time: root.querySelector('[data-sr-time]'),
      score: root.querySelector('[data-sr-score]'),
      combo: root.querySelector('[data-sr-combo]'),
      grid: root.querySelector('[data-sr-grid]'),
      start: root.querySelector('[data-sr-start]'),
      end: root.querySelector('[data-sr-end]'),
      playBtn: root.querySelector('[data-sr-play]'),
      replayBtn: root.querySelector('[data-sr-replay]'),
      finalScore: root.querySelector('[data-sr-final-score]'),
      bestScore: root.querySelector('[data-sr-best-score]'),
      record: root.querySelector('[data-sr-record]'),
      startBottle: root.querySelector('[data-sr-start-bottle]'),
      soundBtn: root.querySelector('[data-sr-sound]'),
      form: root.querySelector('[data-sr-form]'),
      formEl: root.querySelector('[data-sr-form-el]'),
      formError: root.querySelector('[data-sr-form-error]'),
      board: root.querySelector('[data-sr-board]'),
      boardTitle: root.querySelector('[data-sr-board-title]'),
      sending: root.querySelector('[data-sr-sending]'),
      promo: root.querySelector('[data-sr-promo]')
    };

    var audio = createAudio();
    var cells = [];
    var timers = [];
    var promoShown = false;
    var promoTimer = 0; // hors de `timers`, que clearTimers() purge au rejeu

    var state = {
      running: false,
      score: 0,
      hits: 0,
      streak: 0,
      multiplier: 1,
      endsAt: 0,
      roundEndsAt: 0,
      cols: 0,
      targets: {}, // index -> 'normal' | 'golden', sauces encore à ramasser
      remaining: 0,
      lastTargets: {}, // sauces du tour précédent, pour éviter les répétitions
      traps: {},
      timeBonusIndex: -1,
      rafId: 0,
      lastShownTime: -1,
      startedAt: 0, // les bonus temps allongent la partie : on mesure le réel
      bestMultiplier: 1
    };

    setGridSize(LEVELS[0].grid);

    initPixel();
    preloadImages();
    paintStartBottle();
    renderBest();
    renderSoundBtn();

    /* ---- Rendu statique ------------------------------------------------ */

    function preloadImages() {
      var urls = [targetSauce.image];
      trapItems.forEach(function (item) {
        urls.push(item.image);
      });
      urls.forEach(function (url) {
        if (url) {
          var img = new Image();
          img.src = url;
        }
      });
    }

    function paintStartBottle() {
      if (!el.startBottle) return;
      paintItem(
        {
          img: el.startBottle.querySelector('.sr-cell__img'),
          emoji: el.startBottle.querySelector('.sr-cell__emoji')
        },
        targetSauce
      );
    }

    function renderBest() {
      if (el.bestScore) el.bestScore.textContent = formatScore(getBest());
    }

    function renderSoundBtn() {
      if (!el.soundBtn) return;
      var on = audio.isEnabled();
      el.soundBtn.textContent = on ? '🔊' : '🔇';
      el.soundBtn.setAttribute('aria-label', on ? 'Couper le son' : 'Activer le son');
      el.soundBtn.setAttribute('aria-pressed', on ? 'true' : 'false');
    }

    function getBest() {
      return parseInt(storageGet(BEST_SCORE_KEY), 10) || 0;
    }

    /* ---- Grille -------------------------------------------------------- */

    // Reconstruit la grille uniquement quand le nombre de colonnes change.
    function setGridSize(cols) {
      if (state.cols === cols) return;
      clearTimers(); // les flashs en cours pointent sur les cases qu'on remplace
      state.cols = cols;
      el.grid.style.setProperty('--sr-cols', cols);
      cells = buildGrid(el.grid, cols * cols);
    }

    function buildGrid(grid, count) {
      var list = [];
      grid.innerHTML = '';
      for (var i = 0; i < count; i++) {
        var cell = document.createElement('button');
        cell.type = 'button';
        cell.className = 'sr-cell';
        cell.dataset.index = String(i);
        cell.setAttribute('aria-label', 'Case ' + (i + 1));

        var img = document.createElement('img');
        img.className = 'sr-cell__img';
        img.alt = '';
        img.hidden = true;
        img.draggable = false;

        var emoji = document.createElement('span');
        emoji.className = 'sr-cell__emoji';
        emoji.setAttribute('aria-hidden', 'true');

        var float = document.createElement('span');
        float.className = 'sr-cell__float';
        float.setAttribute('aria-hidden', 'true');

        cell.appendChild(img);
        cell.appendChild(emoji);
        cell.appendChild(float);
        grid.appendChild(cell);

        list.push({ el: cell, img: img, emoji: emoji, float: float });
      }
      return list;
    }

    function paintItem(cellRefs, item) {
      if (!item) {
        cellRefs.img.hidden = true;
        cellRefs.img.removeAttribute('src');
        cellRefs.emoji.textContent = '';
        return;
      }
      if (item.image) {
        cellRefs.img.src = item.image;
        cellRefs.img.alt = item.name || '';
        cellRefs.img.hidden = false;
        cellRefs.emoji.textContent = '';
      } else {
        cellRefs.img.hidden = true;
        cellRefs.img.removeAttribute('src');
        cellRefs.emoji.textContent = item.emoji || '?';
      }
    }

    function clearGrid() {
      cells.forEach(function (cell) {
        paintItem(cell, null);
        cell.el.classList.remove('is-target', 'is-trap', 'is-golden', 'is-timebonus');
      });
    }

    /* ---- Tour de jeu --------------------------------------------------- */

    function nextRound() {
      var level = levelFor(state.hits);
      setGridSize(level.grid);

      var slots = [];
      for (var s = 0; s < cells.length; s++) slots.push(s);

      // On tire d'abord les sauces hors des cases qu'elles viennent de quitter.
      var previous = state.lastTargets;
      var fresh = shuffle(
        slots.filter(function (i) {
          return !previous[i];
        })
      );
      var reuse = shuffle(
        slots.filter(function (i) {
          return previous[i];
        })
      );
      var pool = fresh.concat(reuse);

      clearGrid();
      state.targets = {};
      state.traps = {};
      state.timeBonusIndex = -1;
      state.remaining = 0;

      var bottles = Math.min(level.bottles, pool.length);
      var placed = {};
      for (var b = 0; b < bottles; b++) {
        var bottleIndex = pool[b];
        var isGolden = Math.random() < GOLDEN_SAUCE_CHANCE;
        state.targets[bottleIndex] = isGolden ? 'golden' : 'normal';
        placed[bottleIndex] = true;
        state.remaining++;
        paintItem(cells[bottleIndex], targetSauce);
        cells[bottleIndex].el.classList.add('is-target');
        if (isGolden) cells[bottleIndex].el.classList.add('is-golden');
      }
      state.lastTargets = placed; // copie : state.targets se vide au fil des clics

      var free = shuffle(
        slots.filter(function (i) {
          return !state.targets[i];
        })
      );

      var usedItems = [];
      for (var i = 0; i < level.traps && i < free.length; i++) {
        var item = pickTrap(usedItems);
        usedItems.push(item);
        var idx = free[i];
        state.traps[idx] = true;
        paintItem(cells[idx], item);
        cells[idx].el.classList.add('is-trap');
      }

      // Bonus temps : rare, se glisse sur une case encore libre après pièges.
      if (Math.random() < TIME_BONUS_CHANCE) {
        var bonusFree = free.slice(level.traps);
        if (bonusFree.length) {
          var bonusIndex = pickOne(bonusFree);
          state.timeBonusIndex = bonusIndex;
          paintItem(cells[bonusIndex], timeBonusItem);
          cells[bonusIndex].el.classList.add('is-timebonus');
        }
      }

      // Plus il y a de sauces à ramasser, plus le tour laisse de temps.
      var delay = level.delay * (1 + (bottles - 1) * BOTTLE_TIME_FACTOR);
      state.roundEndsAt = performance.now() + delay;
    }

    function pickTrap(used) {
      var available = trapItems.filter(function (item) {
        return used.indexOf(item) === -1;
      });
      return pickOne(available.length ? available : trapItems);
    }

    function shuffle(arr) {
      for (var i = arr.length - 1; i > 0; i--) {
        var j = randInt(i + 1);
        var tmp = arr[i];
        arr[i] = arr[j];
        arr[j] = tmp;
      }
      return arr;
    }

    /* ---- Interactions -------------------------------------------------- */

    el.grid.addEventListener('pointerdown', function (event) {
      var cellEl = event.target.closest('.sr-cell');
      if (!cellEl || !state.running) return;
      event.preventDefault();
      handleTap(parseInt(cellEl.dataset.index, 10));
    });

    // Sécurité clavier / navigateurs sans pointer events.
    el.grid.addEventListener('click', function (event) {
      var cellEl = event.target.closest('.sr-cell');
      if (!cellEl || !state.running) return;
      if (event.detail !== 0) return; // déjà traité par pointerdown
      handleTap(parseInt(cellEl.dataset.index, 10));
    });

    function handleTap(index) {
      if (state.targets[index]) {
        onGoodHit(index);
      } else if (index === state.timeBonusIndex) {
        onTimeBonus(index);
      } else if (state.traps[index]) {
        onWrongHit(index);
      }
      // Case vide : aucun effet, on laisse le joueur continuer.
    }

    function onGoodHit(index) {
      var isGolden = state.targets[index] === 'golden';

      state.hits += 1;
      state.streak += 1;
      state.multiplier = multiplierFor(state.streak);
      if (state.multiplier > state.bestMultiplier) state.bestMultiplier = state.multiplier;

      var gained = BASE_POINTS * state.multiplier;
      if (isGolden) gained *= GOLDEN_SAUCE_MULTIPLIER;
      state.score += gained;

      delete state.targets[index];
      state.remaining -= 1;

      flash(index, isGolden ? 'is-golden-hit' : 'is-good', '+' + gained);
      audio.play(isGolden ? 'golden' : 'good');
      renderHud(true);

      if (state.remaining > 0) {
        // Il reste des sauces dans ce tour : on vide juste la case ramassée.
        paintItem(cells[index], null);
        cells[index].el.classList.remove('is-target', 'is-golden');
      } else {
        nextRound();
      }
    }

    function onTimeBonus(index) {
      state.endsAt += TIME_BONUS_SECONDS * 1000;
      state.timeBonusIndex = -1;

      paintItem(cells[index], null);
      cells[index].el.classList.remove('is-timebonus');

      flash(index, 'is-time', '+' + TIME_BONUS_SECONDS + 's');
      audio.play('bonus');
      // Ni score, ni combo, ni tour suivant : c'est un bonus indépendant.
    }

    function onWrongHit(index) {
      state.score = Math.max(0, state.score - WRONG_CLICK_PENALTY);
      state.streak = 0;
      state.multiplier = 1;

      flash(index, 'is-bad', '-' + WRONG_CLICK_PENALTY);
      audio.play('wrong');
      renderHud(false);
      nextRound();
    }

    function onMiss() {
      state.streak = 0;
      state.multiplier = 1;
      renderHud(false);
      nextRound();
    }

    function flash(index, className, text) {
      var cell = cells[index];
      cell.el.classList.add(className);
      cell.float.textContent = text;
      cell.float.classList.add('is-on');
      timers.push(
        setTimeout(function () {
          cell.el.classList.remove(className);
          cell.float.classList.remove('is-on');
          cell.float.textContent = '';
        }, FEEDBACK_MS)
      );
    }

    /* ---- HUD ----------------------------------------------------------- */

    function renderHud(pulse) {
      el.score.textContent = formatScore(state.score);
      el.combo.textContent = 'COMBO x' + state.multiplier;
      el.combo.classList.toggle('is-hot', state.multiplier > 1);
      if (pulse) {
        el.combo.classList.remove('is-pulse');
        void el.combo.offsetWidth; // reflow pour relancer la transition
        el.combo.classList.add('is-pulse');
      }
    }

    function renderTime(secondsLeft) {
      if (secondsLeft === state.lastShownTime) return;
      state.lastShownTime = secondsLeft;
      el.time.textContent = secondsLeft + ' sec';
      el.time.classList.toggle('is-urgent', secondsLeft <= 5);
    }

    /* ---- Boucle -------------------------------------------------------- */

    function loop(now) {
      if (!state.running) return;

      var remaining = state.endsAt - now;
      if (remaining <= 0) {
        renderTime(0);
        endGame();
        return;
      }

      renderTime(Math.ceil(remaining / 1000));

      if (now >= state.roundEndsAt) onMiss();

      state.rafId = requestAnimationFrame(loop);
    }

    /* ---- Cycle de partie ------------------------------------------------ */

    function startGame() {
      clearTimers();
      clearTimeout(promoTimer);
      state.running = true;
      state.score = 0;
      state.hits = 0;
      state.streak = 0;
      state.multiplier = 1;
      state.targets = {};
      state.lastTargets = {};
      state.remaining = 0;
      state.timeBonusIndex = -1;
      state.lastShownTime = -1;
      state.bestMultiplier = 1;
      state.startedAt = performance.now();
      state.endsAt = state.startedAt + GAME_DURATION * 1000;

      setGridSize(LEVELS[0].grid);
      root.classList.add('is-playing');
      el.start.hidden = true;
      el.end.hidden = true;

      renderHud(false);
      renderTime(GAME_DURATION);
      nextRound();

      cancelAnimationFrame(state.rafId);
      state.rafId = requestAnimationFrame(loop);
    }

    function endGame() {
      state.running = false;
      cancelAnimationFrame(state.rafId);
      clearTimers();
      clearGrid();
      root.classList.remove('is-playing');

      var best = getBest();
      var isRecord = state.score > best;
      if (isRecord) {
        best = state.score;
        storageSet(BEST_SCORE_KEY, String(best));
        audio.play('record');
      }

      el.finalScore.textContent = formatScore(state.score);
      el.bestScore.textContent = formatScore(best);
      if (el.record) el.record.hidden = !isRecord;
      el.end.hidden = false;

      submitScore(state.score, isRecord);
      schedulePromo();
    }

    /* ---- Pub de fin de partie ------------------------------------------- */

    /* Affichée une seule fois par visite : la revoir à chaque partie
       transformerait le rejeu en parcours d'obstacles. */
    function schedulePromo() {
      if (!el.promo || promoShown) return;

      var delay = parseInt(el.promo.getAttribute('data-sr-promo-delay'), 10);
      if (isNaN(delay) || delay < 0) delay = 3;

      clearTimeout(promoTimer);
      promoTimer = setTimeout(function () {
        // Marqué vu seulement à l'affichage : un rejeu avant l'échéance
        // annule l'ouverture sans consommer l'unique passage de la pub.
        promoShown = true;
        el.promo.hidden = false;
        // Laisse un frame au navigateur pour prendre l'état initial,
        // sinon la transition d'ouverture ne joue pas.
        requestAnimationFrame(function () {
          el.promo.classList.add('is-open');
        });
      }, delay * 1000);
    }

    function closePromo() {
      if (!el.promo) return;
      el.promo.classList.remove('is-open');
      setTimeout(function () {
        el.promo.hidden = true;
      }, 280);
    }

    /* ---- Inscription, envoi du score et classement ----------------------- */

    /* La partie ne démarre qu'une fois le joueur identifié : sans email,
       impossible de le recontacter s'il remporte la trottinette. */
    function requestPlay() {
      document.body.classList.add('sr-game-open');
      if (getPlayer()) {
        startGame();
        return;
      }
      if (!el.form) {
        startGame(); // formulaire absent du markup : on ne bloque pas le jeu
        return;
      }
      el.start.hidden = true;
      el.end.hidden = true;
      el.form.hidden = false;
      var first = el.form.querySelector('input');
      if (first) first.focus();
    }

    function handleRegister(event) {
      event.preventDefault();
      var data = {
        prenom: valueOf('prenom'),
        nom: valueOf('nom'),
        email: valueOf('email'),
        pays: valueOf('pays')
      };
      var consent = el.formEl.querySelector('[name="consent"]');

      if (!data.prenom || !data.nom || !data.email) {
        return showFormError('Prénom, nom et email sont obligatoires.');
      }
      if (!isEmail(data.email)) {
        return showFormError('Cette adresse email n’est pas valide.');
      }
      if (consent && !consent.checked) {
        return showFormError('Merci d’accepter le règlement pour jouer.');
      }

      showFormError('');
      savePlayer(data);
      sendToSheet('registration', {});
      track('Lead', {
        content_name: 'Nawhal’s Sauce Rush',
        content_category: 'game_registration'
      });

      el.form.hidden = true;
      startGame();
    }

    function valueOf(name) {
      var input = el.formEl.querySelector('[name="' + name + '"]');
      return input ? input.value.trim() : '';
    }

    function showFormError(message) {
      if (el.formError) el.formError.textContent = message;
    }

    function submitScore(score, isRecord) {
      var player = getPlayer();
      if (!player) return;

      // Durée réellement jouée : la base plus les bonus temps ramassés.
      var durationMs = state.startedAt ? performance.now() - state.startedAt : 0;

      pushBoard({
        name: initialsOf(player.prenom, player.nom),
        email: player.email,
        score: score,
        date: new Date().toISOString()
      });

      if (el.sending) {
        el.sending.hidden = false;
        el.sending.textContent = 'Score enregistré ✓';
      }

      track('SauceRushScore', {
        content_name: 'Nawhal’s Sauce Rush',
        value: score,
        secondes: Math.round(durationMs / 1000)
      }, true);

      sendToSheet('score', {
        score: score,
        duree: formatDuration(durationMs),
        secondes: Math.round(durationMs / 1000),
        ms: Math.round(durationMs),
        sauces: state.hits,
        combo_max: state.bestMultiplier,
        record: isRecord ? 'oui' : 'non',
        meilleur: getBest()
      });

      // La feuille vient d'être écrite : on relit pour situer ce score.
      refreshBoard(score);
    }

    /**
     * Classement affiché : la feuille fait foi. Si elle est injoignable,
     * on retombe sur l'historique local plutôt que de ne rien montrer.
     */
    function refreshBoard(myScore) {
      loadOnlineBoard(BOARD_MAX, function (top) {
        if (top && top.length) {
          renderBoard(top, myScore, true);
        } else {
          renderBoard(readBoard(), myScore, false);
        }
      });
    }

    function renderBoard(board, myScore, isOnline) {
      if (!el.board) return;
      var rows = (board || []).slice(0, BOARD_MAX);
      if (!rows.length) {
        el.board.hidden = true;
        if (el.boardTitle) el.boardTitle.hidden = true;
        return;
      }
      el.board.innerHTML = '';
      var myShown = false;
      rows.forEach(function (entry, i) {
        var li = document.createElement('li');
        li.className = 'sr-board__row';
        // Une seule ligne surlignée : celle du score qui vient d'être joué.
        if (!myShown && Number(entry.score) === Number(myScore)) {
          li.classList.add('is-me');
          myShown = true;
        }
        li.innerHTML =
          '<span class="sr-board__rank">' + (i + 1) + '</span>' +
          '<span class="sr-board__name"></span>' +
          '<span class="sr-board__pts">' + formatScore(entry.score) + '</span>';
        // textContent : les initiales viennent d'une source externe.
        li.querySelector('.sr-board__name').textContent = entry.initiales || entry.name || '—';
        el.board.appendChild(li);
      });
      el.board.hidden = false;
      if (el.boardTitle) {
        el.boardTitle.hidden = false;
        el.boardTitle.textContent = isOnline ? 'Top 5 en ligne' : 'Tes derniers scores';
      }
    }

    function clearTimers() {
      timers.forEach(clearTimeout);
      timers = [];
      cells.forEach(function (cell) {
        cell.el.classList.remove('is-good', 'is-bad', 'is-golden-hit', 'is-time');
        cell.float.classList.remove('is-on');
        cell.float.textContent = '';
      });
    }

    /* ---- Boutons -------------------------------------------------------- */

    el.playBtn.addEventListener('click', requestPlay);
    el.replayBtn.addEventListener('click', requestPlay);

    if (el.formEl) el.formEl.addEventListener('submit', handleRegister);

    if (el.promo) {
      el.promo.addEventListener('click', function (event) {
        // Le voile ferme aussi : cliquer à côté d'une pub doit la fermer.
        if (event.target.closest('[data-sr-promo-close]') || event.target === el.promo) {
          closePromo();
        }
      });
      document.addEventListener('keydown', function (event) {
        if (event.key === 'Escape' && !el.promo.hidden) closePromo();
      });
    }

    if (el.soundBtn) {
      el.soundBtn.addEventListener('click', function () {
        audio.toggle();
        renderSoundBtn();
      });
    }

    // Onglet quitté en pleine partie : on arrête proprement.
    document.addEventListener('visibilitychange', function () {
      if (document.hidden && state.running) endGame();
    });
  }

  /* =========================================================================
   * 5. CONFIG INJECTÉE (Shopify) — data-sr-config='{"target":…,"traps":[…]}'
   * ====================================================================== */

  function applyInlineConfig(root) {
    var raw = root.getAttribute('data-sr-config');
    if (!raw) return;
    var config;
    try {
      config = JSON.parse(raw);
    } catch (e) {
      return;
    }
    if (config.target) {
      if (config.target.name) targetSauce.name = config.target.name;
      if (config.target.image) targetSauce.image = config.target.image;
      if (config.target.emoji) targetSauce.emoji = config.target.emoji;
    }
    if (Array.isArray(config.traps) && config.traps.length) {
      config.traps.forEach(function (trap, i) {
        if (!trap || !trap.image || !trapItems[i]) return;
        trapItems[i].image = trap.image;
        if (trap.name) trapItems[i].name = trap.name;
      });
    }
    if (config.duration) GAME_DURATION = parseInt(config.duration, 10) || GAME_DURATION;
    if (config.webhook) {
      if (config.webhook.url) WEBHOOK.url = config.webhook.url;
      if (config.webhook.token) WEBHOOK.token = config.webhook.token;
      if (config.webhook.game) WEBHOOK.game = config.webhook.game;
    }
    if (config.pixel && config.pixel.id) PIXEL.id = config.pixel.id;
  }

  /* =========================================================================
   * 6. BOOT
   * ====================================================================== */

  function boot() {
    document.querySelectorAll('[data-sauce-rush]').forEach(initGame);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Shopify : ré-init si la section est rechargée dans l'éditeur de thème.
  document.addEventListener('shopify:section:load', boot);
})();
